import { OUT_OF_ORDER_ROOMS_DEFINITION } from './out-of-order-rooms.definition';
import { PAYMENT_REFUNDS_DEF } from './payment-refunds.definition';
import { CASHIER_SUMMARY_DEFINITION } from './cashier-summary.definition';
import { PMS_REVENUE_ADJUSTMENTS_DEFINITION } from './pms-revenue-adjustments.definition';
import { OPEN_FOLIOS_DEFINITION } from './open-folios.definition';
import { HOUSEKEEPING_VARIANCE_DEFINITION } from './housekeeping-variance.definition';
import { TA_COMMS_DEFINITION } from './ta-comms.definition';
import { PAID_OUTS_DEFINITION } from './paid-outs.definition';
import { GUEST_CREDIT_LIMIT_DEFINITION } from './guest-credit-limit.definition';
import { COMPLIMENTARY_ROOMS_DEFINITION } from './complimentary-rooms.definition';
import { PM_ACCOUNTS_DEFINITION } from './pm-accounts.definition';
import { AR_ACTIVITY_DEFINITION } from './ar-activity.definition';
import { NO_SHOWS_DEFINITION } from './no-shows.definition';

export const PMS_REPORT_TYPE_IDS = {
   OUT_OF_ORDER_ROOMS: 1,
   RATE_CHANGES: 2,
   OPEN_FOLIOS: 3,
   GUEST_CREDIT_LIMIT: 4,
   HOUSEKEEPING_VARIANCE: 5,
   COMPLIMENTARY_ROOMS: 6,
   NO_SHOWS: 7,
   PM_ACCOUNTS: 8,
   PAYMENT_REFUNDS: 9,
   PAID_OUTS: 10,
   PMS_REVENUE_ADJUSTMENTS: 11,
   TA_COMMS: 12,
   CASHIER_SUMMARY: 13,
   AR_ACTIVITY: 14,
   OUT_OF_SERVICE_ROOMS: 15
} as const;

export type PmsReportTypeId =
   (typeof PMS_REPORT_TYPE_IDS)[keyof typeof PMS_REPORT_TYPE_IDS];

export const PMS_REPORT_TYPE_NAMES: Record<PmsReportTypeId, string> = {
   [PMS_REPORT_TYPE_IDS.OUT_OF_ORDER_ROOMS]: 'Out of Order Rooms',
   [PMS_REPORT_TYPE_IDS.RATE_CHANGES]: 'Rate Changes',
   [PMS_REPORT_TYPE_IDS.OPEN_FOLIOS]: 'Open Folios',
   [PMS_REPORT_TYPE_IDS.GUEST_CREDIT_LIMIT]: 'Guest Credit Limit',
   [PMS_REPORT_TYPE_IDS.HOUSEKEEPING_VARIANCE]: 'Housekeeping Variance',
   [PMS_REPORT_TYPE_IDS.COMPLIMENTARY_ROOMS]: 'Complimentary Rooms',
   [PMS_REPORT_TYPE_IDS.NO_SHOWS]: 'No Shows',
   [PMS_REPORT_TYPE_IDS.PM_ACCOUNTS]: 'PM Accounts',
   [PMS_REPORT_TYPE_IDS.PAYMENT_REFUNDS]: 'Payment Refunds',
   [PMS_REPORT_TYPE_IDS.PAID_OUTS]: 'Paid Outs',
   [PMS_REPORT_TYPE_IDS.PMS_REVENUE_ADJUSTMENTS]: 'PMS Revenue Adjustments',
   [PMS_REPORT_TYPE_IDS.TA_COMMS]: 'TA Comms',
   [PMS_REPORT_TYPE_IDS.CASHIER_SUMMARY]: 'Cashier Summary',
   [PMS_REPORT_TYPE_IDS.AR_ACTIVITY]: 'AR Activity',
   [PMS_REPORT_TYPE_IDS.OUT_OF_SERVICE_ROOMS]: 'Out of Service Rooms'
};

/**
 * Only map report types that currently have an implemented HTML report definition.
 *
 * This is deliberately NOT all PMS report types.
 * Adding all IDs here before their definitions exist would either break the build
 * or make the viewer pretend it supports reports it cannot render correctly yet.
 */
const REPORT_TYPE_BY_PMS_REPORT_TYPE_ID: Partial<Record<PmsReportTypeId, string>> = {
   [PMS_REPORT_TYPE_IDS.OUT_OF_ORDER_ROOMS]: OUT_OF_ORDER_ROOMS_DEFINITION.reportType,

   [PMS_REPORT_TYPE_IDS.PAYMENT_REFUNDS]: PAYMENT_REFUNDS_DEF.reportType,

   [PMS_REPORT_TYPE_IDS.CASHIER_SUMMARY]: CASHIER_SUMMARY_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.PMS_REVENUE_ADJUSTMENTS]:
      PMS_REVENUE_ADJUSTMENTS_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.OPEN_FOLIOS]: OPEN_FOLIOS_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.HOUSEKEEPING_VARIANCE]:
      HOUSEKEEPING_VARIANCE_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.TA_COMMS]: TA_COMMS_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.PAID_OUTS]: PAID_OUTS_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.GUEST_CREDIT_LIMIT]: GUEST_CREDIT_LIMIT_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.COMPLIMENTARY_ROOMS]:
      COMPLIMENTARY_ROOMS_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.PM_ACCOUNTS]: PM_ACCOUNTS_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.AR_ACTIVITY]: AR_ACTIVITY_DEFINITION.reportType,
   [PMS_REPORT_TYPE_IDS.NO_SHOWS]: NO_SHOWS_DEFINITION.reportType
};

const PMS_REPORT_TYPE_ID_VALUES = Object.values(PMS_REPORT_TYPE_IDS) as number[];

export function isKnownPmsReportTypeId(
   pmsReportTypeId: number | null | undefined
): pmsReportTypeId is PmsReportTypeId {
   if (pmsReportTypeId === null || pmsReportTypeId === undefined) return false;

   return PMS_REPORT_TYPE_ID_VALUES.includes(pmsReportTypeId);
}

export function getPmsReportTypeName(
   pmsReportTypeId: number | null | undefined
): string | null {
   if (!isKnownPmsReportTypeId(pmsReportTypeId)) return null;

   return PMS_REPORT_TYPE_NAMES[pmsReportTypeId];
}

export function getReportTypeByPmsReportTypeId(
   pmsReportTypeId: number | null | undefined
): string | null {
   if (!isKnownPmsReportTypeId(pmsReportTypeId)) return null;

   return REPORT_TYPE_BY_PMS_REPORT_TYPE_ID[pmsReportTypeId] ?? null;
}

export function isSupportedHtmlReportType(
   pmsReportTypeId: number | null | undefined
): boolean {
   return getReportTypeByPmsReportTypeId(pmsReportTypeId) !== null;
}
